// @spec SHL-STR-005
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Subscribe to media query changes.
 */
function subscribe(query: MediaQueryList, callback: () => void): () => void {
  query.addEventListener('change', callback);
  return () => {
    query.removeEventListener('change', callback);
  };
}

/**
 * Hook for detecting whether a media query matches.
 *
 * Uses useSyncExternalStore for proper hydration and server-side rendering.
 *
 * @param query - CSS media query string (e.g., "(min-width: 768px)")
 * @returns Whether the media query currently matches
 *
 * @example
 * function ResponsiveComponent() {
 *   const isDesktop = useMediaQuery("(min-width: 768px)");
 *
 *   return isDesktop ? <DesktopView /> : <MobileView />;
 * }
 */
export function useMediaQuery(query: string): boolean {
  const [mediaQuery, setMediaQuery] = useState<MediaQueryList | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(query);
    setMediaQuery(mq);
  }, [query]);

  const subscribeToQuery = useCallback(
    (callback: () => void) => {
      if (!mediaQuery) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
      }
      return subscribe(mediaQuery, callback);
    },
    [mediaQuery]
  );

  const getSnapshot = useCallback(() => {
    if (!mediaQuery) return false;
    return mediaQuery.matches;
  }, [mediaQuery]);

  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribeToQuery, getSnapshot, getServerSnapshot);
}
